import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, PhoneForwarded, Voicemail, Bot, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';

// v3.21-1840 · READ-ONLY "Who Will Ring" — rendered from the live hunt's own eligibility/order code.
const REASON_TEXT = {
  inactive: 'Not Active', routing_off: 'Routing is OFF', no_phone: 'No phone number', invalid_phone: 'Phone number invalid',
  no_order: 'No Order set (others have one)', 'company_mismatch:unset': 'No company line selected',
};
const reasonText = (r) => REASON_TEXT[r]
  || (r.startsWith('unavailable:') ? `Availability is "${r.split(':')[1]}"` : r.startsWith('company_mismatch:') ? 'This company line not selected' : r);
const CONFLICT_TEXT = {
  phone_is_final_fallback_number: 'Phone equals the final fallback number',
  phone_is_a_hub_line: 'Phone is a Hub line (loop)', phone_shared_with_another_agent: 'Phone shared with another agent',
};
const yes = (v) => (v ? 'Yes' : 'No');

const LineCard = ({ line, ff }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={`wwr-line-${line.brand}`}>
    <div className="text-xs font-semibold text-slate-700 mb-2">{line.label} <span className="text-slate-400 font-normal">(…{line.hub_number_last4})</span></div>
    <ol className="space-y-1">
      {line.sequence.map((s) => (
        <li key={s.id} className="flex items-center gap-2 text-xs" data-testid={`wwr-step-${line.brand}-${s.step}`}>
          <span className="h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px]">{s.step}</span>
          <span className="font-medium text-slate-800">{s.name}</span>
          <span className="text-slate-400">ext {s.extension || '—'} · …{s.phone_last4}</span>
          {s.ai_agent && <span className="inline-flex items-center gap-0.5 text-violet-600"><Bot className="h-3 w-3" />AI</span>}
          {s.press1 && <span className="text-amber-600">Press 1</span>}
        </li>
      ))}
      <li className="flex items-center gap-2 text-xs" data-testid={`wwr-final-${line.brand}`}>
        <PhoneForwarded className="h-4 w-4 text-slate-500" />
        {ff.active ? <span>Final destination …{ff.configured_last4}{ff.press1 ? ' (Press 1)' : ''}</span>
          : <span className="text-amber-700">No final destination{ff.blocked_reason ? ' (blocked: invalid or Hub line)' : ''}</span>}
      </li>
      <li className="flex items-center gap-2 text-xs"><Voicemail className="h-4 w-4 text-slate-500" />PTC Hub voicemail</li>
    </ol>
  </div>
);

const AgentRow = ({ a }) => {
  const eqf = a.reasons_by_line.eqf_hub || [];
  return (
    <tr className="border-b border-slate-50 align-top" data-testid={`wwr-agent-${a.id}`}>
      <td className="px-2 py-1.5">{a.order ?? '—'}{a.order != null && a.order_type !== 'int' && <span className="text-red-600"> ({a.order_type})</span>}</td>
      <td className="px-2 py-1.5 font-medium text-slate-800">{a.name} <span className="text-slate-400 font-normal">{a.extension ? `ext ${a.extension}` : ''}</span></td>
      <td className="px-2 py-1.5">{yes(a.active)}</td>
      <td className="px-2 py-1.5">{a.availability}</td>
      <td className="px-2 py-1.5">{yes(a.routing)}</td>
      <td className="px-2 py-1.5">{yes(a.primary)}</td>
      <td className="px-2 py-1.5">{yes(a.ai_agent)}</td>
      <td className="px-2 py-1.5">{yes(a.press1)}</td>
      <td className="px-2 py-1.5">{a.company_lines.join(', ') || '—'}</td>
      <td className="px-2 py-1.5">{a.phone_last4 ? `…${a.phone_last4}` : '—'} {a.phone_valid ? '' : <span className="text-red-600">invalid</span>}</td>
      <td className="px-2 py-1.5" data-testid={`wwr-agent-reason-${a.id}`}>
        {eqf.length === 0 ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Eligible</span>
          : <span className="text-red-700">{eqf.map(reasonText).join(' · ')}</span>}
        {a.conflicts.map((c) => <div key={c} className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{CONFLICT_TEXT[c] || c}</div>)}
      </td>
    </tr>
  );
};

export const WhoWillRingPanel = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/telephony/routing-preview'); setData(r.data); setError(''); }
    catch (e) { setError(e?.response?.data?.detail || 'Could not load routing preview'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (error) return <div className="text-xs text-slate-500 p-3" data-testid="wwr-error">{error}</div>;
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mt-4" data-testid="who-will-ring-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">Who Will Ring (no extension entered)</div>
          <div className="text-[10px] text-slate-400">Read-only · same rules as live call routing · {new Date(data.generated_at).toLocaleTimeString()}</div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="wwr-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mb-4">{data.lines.map((l) => <LineCard key={l.brand} line={l} ff={data.final_forward} />)}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="border-b border-slate-100 text-left text-slate-500">
            {['Order', 'Agent', 'Active', 'Availability', 'Routing', 'Primary', 'AI', 'Press 1', 'Company lines', 'Phone', 'EQF Hub line status'].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>{data.agents.map((a) => <AgentRow key={a.id} a={a} />)}</tbody>
        </table>
      </div>
    </div>
  );
};

export default WhoWillRingPanel;
